"use client";

import { useState } from "react";

import Image from "next/image";

import { cn } from "@/lib/utils/index";

interface AvatarProps {
  /** Display name — used for initials fallback + accessible label. */
  name: string;
  /** Profile picture URL, or null to render initials (or {@link fallbackIcon}). */
  src?: string | null;
  /** Pixel size of the square avatar. */
  size?: number;
  className?: string;
  /** `"circle"` (default) for a person's photo — cropped `object-cover`. `"square"` for a
   *  non-person mark (e.g. a company logo/icon) whose source image is already square-ish and
   *  shouldn't be circle-cropped — rendered `object-contain` instead. */
  shape?: "circle" | "square";
  /** Replaces the initials fallback (shown when there's no `src`, or it fails to load) with an
   *  icon — for a facet that isn't a person and so has no meaningful initials (e.g. a company). */
  fallbackIcon?: React.ReactNode;
}

/** Derive up to two uppercase initials from a name. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Avatar: profile image when present and loadable, initials chip otherwise (or a
 * {@link AvatarProps.fallbackIcon} in place of initials). If the image URL fails to load (e.g.
 * an expired Google photo URL), it falls back to initials/icon rather than letting the broken
 * `<img>` show its alt text.
 */
export function Avatar({
  name,
  src,
  size = 24,
  className,
  shape = "circle",
  fallbackIcon,
}: AvatarProps): React.ReactElement {
  // State
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);

  // Derived
  const dimension = { width: size, height: size };
  // Scale initials with the avatar so larger sizes (e.g. 36px in the performance views) read correctly.
  const initialsStyle = { ...dimension, fontSize: Math.round(size * 0.42) };
  const showImage = Boolean(src) && src !== erroredSrc;
  const roundedClass = shape === "circle" ? "rounded-full" : "rounded-md";

  // Render
  if (src && showImage) {
    // unoptimized: these avatars are 20-24px; the optimizer round-trip costs more than it saves.
    return (
      <Image
        src={src}
        alt={name}
        {...dimension}
        // Inline width+height pin the box square — without it Tailwind Preflight's
        // `img { height: auto }` lets portrait source photos render as ovals.
        style={dimension}
        unoptimized
        onError={() => setErroredSrc(src)}
        className={cn("shrink-0 object-cover", roundedClass, shape === "square" && "object-contain", className)}
      />
    );
  }

  return (
    <span
      style={initialsStyle}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden bg-indigo-100 font-semibold text-indigo-700",
        roundedClass,
        fallbackIcon && "bg-gray-100 text-gray-400",
        className
      )}
      role="img"
      aria-label={name}
      title={name}
    >
      {fallbackIcon ?? initials(name)}
    </span>
  );
}
